import React, { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { listTariffRates, importTariffRates, deleteTariffRate } from 'services/tariffs.service'
import { useI18n, apiError } from 'i18n'

const PAGE_SIZE = 20

// "94054201" → "9405.42.01"; NICO "9405420101" → "9405.42.01.01".
const formatFraction = (f) => (f || '').replace(/^(\d{4})(\d{2})(\d{2})(\d{2})?$/, (m, a, b, c, d) => [a, b, c, d].filter(Boolean).join('.'))

const AdminTariffs = () => {
  const { t, fmt } = useI18n()
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [skipped, setSkipped] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = { page, pageSize: PAGE_SIZE }
      if (q) params.q = q
      const data = await listTariffRates(params)
      setRows(data.tariffRates || [])
      setTotal(data.total || 0)
    } catch (error) {
      toast.error(apiError(error, t('tariffs.loadFailed')))
    } finally {
      setLoading(false)
    }
  }, [page, q])

  useEffect(() => { load() }, [load])

  const onImport = async (event) => {
    event.preventDefault()
    if (!file) {
      toast.error(t('tariffs.pickFile'))
      return
    }
    setBusy(true)
    try {
      const res = await importTariffRates(file)
      toast.success(t('tariffs.imported', { count: res.imported }))
      setSkipped(res.errors || [])
      if (res.errorCount) toast.error(t('tariffs.importErrors', { count: res.errorCount }))
      setFile(null)
      event.target.reset()
      load()
    } catch (error) {
      setSkipped(error?.response?.data?.errors || [])
      toast.error(apiError(error, t('tariffs.importFailed')))
    } finally {
      setBusy(false)
    }
  }

  const onDelete = async (row) => {
    if (!window.confirm(t('tariffs.confirmDelete', { fraction: formatFraction(row.tariffFraction) }))) return
    try {
      await deleteTariffRate(row.tariffFraction)
      toast.success(t('tariffs.deleted'))
      load()
    } catch (error) {
      toast.error(apiError(error, t('tariffs.deleteFailed')))
    }
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold'>{t('tariffs.title')}</h1>
        <p className='text-sm text-slate-500'>{t('tariffs.subtitle')}</p>
      </div>

      <form onSubmit={onImport} className='space-y-2 rounded-xl border border-dashed border-slate-300 bg-white p-4'>
        <h2 className='text-sm font-semibold text-slate-700'>{t('tariffs.importTitle')}</h2>
        <div className='flex flex-wrap items-center gap-3'>
          <input type='file' accept='.csv,text/csv' onChange={(e) => setFile(e.target.files?.[0] || null)} className='text-sm' />
          <button type='submit' disabled={busy} className='rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600 disabled:opacity-50'>
            {busy ? t('tariffs.importing') : t('tariffs.import')}
          </button>
        </div>
        <p className='text-xs text-slate-500'>{t('tariffs.importHelp')}</p>
        {skipped.length > 0 && (
          <ul className='ml-5 list-disc text-xs text-status-review'>
            {skipped.slice(0, 10).map((e) => <li key={e.line}>#{e.line}: {e.reason}</li>)}
          </ul>
        )}
      </form>

      <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1) }} placeholder={t('tariffs.search')}
        className='w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none' />

      <div className='overflow-x-auto rounded-xl border border-slate-200 bg-white'>
        <table className='min-w-full divide-y divide-slate-200 text-sm'>
          <thead className='bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500'>
            <tr>
              <th className='px-4 py-3'>{t('tariffs.fraction')}</th>
              <th className='px-4 py-3'>{t('tariffs.description')}</th>
              <th className='px-4 py-3 text-right'>{t('tariffs.rate')}</th>
              <th className='px-4 py-3'>{t('tariffs.source')}</th>
              <th className='px-4 py-3'>{t('tariffs.updated')}</th>
              <th className='px-4 py-3 text-right'>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {loading && <tr><td colSpan={6} className='px-4 py-8 text-center text-slate-400'>{t('common.loading')}</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className='px-4 py-8 text-center text-slate-400'>{t('tariffs.empty')}</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.tariffFraction} className='hover:bg-slate-50'>
                <td className='px-4 py-2 font-mono'>{formatFraction(r.tariffFraction)}</td>
                <td className='px-4 py-2 text-slate-600'>{r.description || '—'}</td>
                <td className='px-4 py-2 text-right tabular-nums font-semibold'>{r.igiRate === null ? '—' : fmt.pct(r.igiRate)}</td>
                <td className='px-4 py-2 text-slate-500'>{r.source || '—'}</td>
                <td className='px-4 py-2 text-slate-500'>{fmt.date(r.updatedAt)}</td>
                <td className='px-4 py-2 text-right'>
                  <button onClick={() => onDelete(r)} className='font-semibold text-status-incomplete hover:underline'>{t('common.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='flex items-center justify-between text-sm text-slate-600'>
        <span>{t('common.totalCount', { count: total })}</span>
        <div className='flex items-center gap-3'>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
            className='rounded-lg border border-slate-300 px-3 py-1 font-semibold disabled:opacity-40'>{t('common.prev')}</button>
          <span>{t('common.pageOf', { page, pages })}</span>
          <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}
            className='rounded-lg border border-slate-300 px-3 py-1 font-semibold disabled:opacity-40'>{t('common.next')}</button>
        </div>
      </div>
    </div>
  )
}

export default AdminTariffs
